import { API_IDS } from '@intigral/constant';
import ResponseError from '@intigral/domain/lib/core/entities/errors/ResponseError';
import { toast } from '@intigral/shared';
import { SelectProfile as SelectProfileUi, type SelectProfileHero, type SelectProfileItem } from '@intigral/ui';
import { type IProfileData } from '@intigral/types';
import { get, set } from '@intigral/utils/lib/browser-storage/localStorage';
import { useMediaQuery } from '@intigral/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, parsePath, useLocation, useNavigate } from 'react-router-dom';
import { BRAZE } from '../../config/analytics-events';
import { screenDetailTypes, STORE_NAME } from '../../config/constants';
import { EVENT, KEYS } from '../../contexts/MixpanelConstants';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import {
    getMixpanelProfileList,
    getMixpanelProfileName,
    getMixpanelProfilesCount,
} from '../../contexts/MixpanelHelper';
import h from '../../helpers/common';
import { getSelectedProfileAvatars } from '../../helpers/profile';
import url from '../../helpers/url';
import user from '../../helpers/user';
import useBraze from '../../hooks/useBraze';
import useLanguage from '../../hooks/useLanguage';
import { handleSwitchLanguage } from '../../features/header/utils/headerUtils';
import { useConfigurationSections } from '../../hooks/useConfiguration';
import useProfileAvatars from '../../hooks/useProfileAvatars';
import { useProfileSwitching } from '../../hooks/useProfileSwitching';
import { queueUrlAfterProfileSelect, takeQueuedUrlAfterProfileSelect } from '../../utils/pendingDeepLink';
import { IStore } from '../../interfaces/StoreTypes';
import { handleProfileSelectionPromotions } from '../../managers/dataLoaderHandler';
import {
    ClearDispatchedActions,
    LoadProfilesList,
    selectProfileDone,
    switchProfileMutationFn,
} from '../../store/reducers/profileSlice';
import { __ } from '../../translations';
import { getUserInfo } from '../../utils/user';
import { useProfileList } from '../../utils/useUserProfile';
import PasswordPopup from '../elements/PasswordPopup';

// Configuration is now fetched from the redux store instead of localStorage
// to avoid initialization issues when the user first logs in.

const RouterLink = ({ href, ...props }) => <Link to={href} {...props} />;

export const SelectProfile = ({ data, ...props }) => {
    const showKidsOnly = url.getQuery('kids') ?? false;
    const [showProfiles, setShowProfiles] = useState(false);
    const { data: profileList } = useProfileList(getUserInfo());
    const { dispatchedActions } = useSelector((state: IStore) => state.profile);
    const selectedProfile = useSelector((state: IStore) => state.profile?.selectedProfile) || ({} as IProfileData);
    // Using React Query instead of Redux for configuration
    const { profile: profileConfig, config, promotionProviderImages } = useConfigurationSections();
    const profileColors = profileConfig?.colors || [];
    const {
        track,
        setUserProperties,
        setSuperProperties,
    }: { track: any; setUserProperties: any; setSuperProperties: any } = useContext(MixpanelContext);
    const { logBrazeCustomEvent } = useBraze();
    const dispatch = useDispatch();
    const loadProfilesList = useCallback(() => dispatch(LoadProfilesList(false)), [dispatch]);
    // const loadProfileData = useCallback((profileData) => dispatch(LoadProfile(profileData)), [dispatch]);
    const clearDispatchedActions = useCallback(() => dispatch(ClearDispatchedActions()), [dispatch]);

    const [showDoneBtn, setShowDoneBtn] = useState(false);
    const [showCloseBtn, setShowCloseBtn] = useState(false);
    const [selectedProfileGuid, setSelectedProfileGuid] = useState('');
    const [eligibleToAdd, setEligibleToAdd] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [profileSwitched, setProfileSwitched] = useState(false);

    // password popup states
    const [showPasswordPopup, setShowPasswordPopup] = useState(false);
    const [passwordVerified, setPasswordVerified] = useState(false);
    const [clickedProfile, setClickedProfile] = useState(null);
    const { data: profileAvatars } = useProfileAvatars();
    const isKidsSelect = Boolean(showKidsOnly || selectedProfile.kidsMode);
    const promoApiId = isKidsSelect
        ? API_IDS.POPCORN_API_PROFILE_SELECTION_KIDS_PROMOTIONS
        : API_IDS.POPCORN_API_PROFILE_SELECTION_PROMOTIONS;
    const { data: profileSelectionPromotions } = useQuery({
        queryKey: ['promotions', promoApiId],
        queryFn: () => handleProfileSelectionPromotions(promoApiId),
        enabled: !!getUserInfo('apiKey'),
        staleTime: 1000 * 60 * 5,
        retry: 2,
        refetchOnReconnect: true,
        refetchOnWindowFocus: false,
    });
    const landingPromos = Array.isArray(profileSelectionPromotions)
        ? profileSelectionPromotions.filter(
              (item) => item?.landingImage || item?.image || item?.portrait || item?.landingLogo || item?.logo
          )
        : [];
    const promoSlideDelay = config?.promoSliderSPL?.nextSlideDelay || undefined;
    // read on first render, or a cached promo flips orientation
    const isMobile = useMediaQuery('(max-width: 924px)', undefined, { getInitialValueInEffect: false });
    const imageWidths = config?.imagizer?.components_images;
    const promos: SelectProfileHero[] = landingPromos.map((promo) => {
        const providerKey = Array.isArray(promo?.categories) ? promo.categories[0] : promo?.categories;
        return {
            heroSrc:
                (isMobile
                    ? h.getImagizerUrl(
                          promo?.portrait || promo?.landingImage || promo?.image,
                          imageWidths?.FEATURED_ITEM_MOBILE
                      )
                    : h.getImagizerUrl(promo?.landingImage || promo?.image, imageWidths?.FEATURED_ITEM_DESKTOP)) ||
                undefined,
            titleLogoSrc: promo?.landingLogo || promo?.logo || undefined,
            title: promo?.title || promo?.text || undefined,
            providerLogoSrc: promotionProviderImages?.[providerKey]?.imageUrl || undefined,
        };
    });

    const prevSelectedProfile = get(STORE_NAME.SELECTED_PROFILE) || {};
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const switchProfileMutation = useMutation({
        mutationFn: switchProfileMutationFn,
        onSuccess: ({ result, profile }) => {
            const { identityToken, showWelcomeScreen, liveLikeAccessToken = null, liveLikeProfileId = null } = result;
            const currentStoredUserData = getUserInfo();
            set(STORE_NAME.USER, { ...currentStoredUserData, identityToken });

            // For kids profiles, ensure showWelcomeScreen is true regardless of server response
            // This ensures the gamification welcome modal appears for all kids profiles
            const shouldShowWelcomeScreen = profile.kidsMode ? true : showWelcomeScreen;

            const updatedProfile = {
                ...profile,
                date: Date.now(),
                showWelcomeScreen: shouldShowWelcomeScreen,
                liveLikeAccessToken,
                liveLikeProfileId,
            };
            set(STORE_NAME.SELECTED_PROFILE, updatedProfile);
            const { urlToOpenTheProfile } = useProfileSwitching(profile as any);
            queueUrlAfterProfileSelect(urlToOpenTheProfile);
            dispatch(selectProfileDone(updatedProfile));
        },
        onError: (error: ResponseError | { code?: string } | undefined, variables) => {
            // switchProfileMutationFn throws error.data, so the payload may be either
            // ResponseError or the bare error data object.
            const errCode = (error as ResponseError)?.data?.code ?? (error as { code?: string })?.code;
            // The errorCode '9031' is for "Incorrect password", but for B2B the message should be "Incorrect account PIN"
            const localizedKey = errCode ? (errCode === '9031' ? B2B_localization : '') + `err_msg_${errCode}` : '';
            let localizedError = errCode ? __(localizedKey) : __('general_error_message');
            if (!errCode || localizedKey === localizedError)
                // If no localization found, fallback to general error message
                localizedError = __('general_error_message');
            toast(localizedError, 'error');
            // reset so a retry re-runs the effect; the PIN popup keeps it
            if (!variables?.passwordProtected) {
                setClickedProfile(null);
            }
        },
    });

    // Router remounts after selectProfileDone; queue the dest so we can still leave this page.
    useEffect(() => {
        if (!selectedProfile?.profileGuid) {
            return;
        }
        const nextUrl = takeQueuedUrlAfterProfileSelect();
        if (!nextUrl) {
            return;
        }
        // may predate a language switch
        const { pathname = '/', search = '', hash = '' } = parsePath(nextUrl);
        navigate(h.getAltLanguageURL(h.getLang(), { pathname, search }) + hash, { replace: true });
        queryClient.invalidateQueries({ queryKey: ['profiles'] });
        queryClient.invalidateQueries({ queryKey: ['cms-load'] });
        setProfileSwitched(true);
    }, [selectedProfile?.profileGuid, selectedProfile?.date, navigate, queryClient]);

    const validatePasswordAndSwitchProfile = ({ profileData, password }) => {
        return switchProfileMutation.mutateAsync({
            ...profileData,
            passwordProtected: password,
            profilePassword: password,
        });
    };

    // useEffect(() => {
    //     dispatch(setProfileAvatars(profileAvatars));
    // }, [dispatch, profileAvatars]);

    useEffect(() => {
        // load profile and close popup after password Verified
        if (passwordVerified) {
            setPasswordVerified((s) => {
                setSuperProperties({
                    [KEYS.SUPER.ACTIVE_PROFILE]: clickedProfile.profileName,
                });
                track(EVENT.PROFILE.PROFILES_SELECT, getMixpanelProfileName(clickedProfile.profileName));
                setClickedProfile(null);
                setShowPasswordPopup(false);
                setProfileSwitched(true);
                return false;
            });
        }
    }, [passwordVerified]);

    // load profile or show password popup
    const loadProfileFunctions = async () => {
        setSuperProperties({
            [KEYS.SUPER.ACTIVE_PROFILE]: clickedProfile.profileName,
        });
        track(EVENT.PROFILE.PROFILES_SELECT, getMixpanelProfileName(clickedProfile.profileName));
        switchProfileMutation.mutate(clickedProfile);
    };

    useEffect(() => {
        if (clickedProfile) {
            // const selectedProfile = get(STORE_NAME.SELECTED_PROFILE) ?? selectedProfile;
            if (
                !editMode ||
                (editMode && selectedProfile.kidsMode && clickedProfile.profileGuid !== selectedProfile.profileGuid)
            ) {
                if (clickedProfile.passwordProtected && !passwordVerified) {
                    setShowPasswordPopup(true);
                } else {
                    loadProfileFunctions();
                }
            } else {
                track(EVENT.PROFILE.PROFILES_EDIT, getMixpanelProfileName(clickedProfile.profileName));
            }
        }
    }, [clickedProfile]);

    useEffect(() => {
        // enable edit mode if user navigate from menu
        if (location && typeof location.state !== 'undefined' && location.state?.editMode) {
            setEditMode(true);
            setShowDoneBtn(true);
        }
    }, [location.pathname, location.search]);

    const handleEditProfiles = () => {
        setEditMode(true);
        setShowDoneBtn(true);
    };

    const handleEditProfilesDone = () => {
        setEditMode(false);
        setShowDoneBtn(false);
    };

    const closePasswordPopupHandler = () => {
        setShowPasswordPopup(false);
        setClickedProfile(null);
    };

    const selectProfileHandler = (clickedProfile) => {
        if (editMode) {
            if (selectedProfile.kidsMode && clickedProfile.profileGuid === selectedProfile.profileGuid) {
                navigate(url.route(`edit-profile?guid=${btoa(clickedProfile.profileGuid)}`));
            }

            if (!selectedProfile.kidsMode) {
                navigate(url.route(`edit-profile?guid=${btoa(clickedProfile.profileGuid)}`));
            }
        } else {
            setClickedProfile(clickedProfile);
            logBrazeCustomEvent(BRAZE.EVENTS.APP_OPEN);
        }
    };

    useEffect(() => {
        if (profileList && !editMode) {
            setUserProperties({
                [KEYS.PEOPLE.USER_PROFILES]: getMixpanelProfileList(profileList?.profiles),
            });
            track(EVENT.PROFILE.PROFILES_VIEW, getMixpanelProfilesCount(Object.values(profileList?.profiles).length));
        }
    }, [editMode]);

    useEffect(() => {
        if (profileList !== null) {
            if (selectedProfile && typeof selectedProfile.profileGuid !== 'undefined') {
                setSelectedProfileGuid(selectedProfile.profileGuid);
                setShowCloseBtn(true);
            }
        } else {
            loadProfilesList();
        }
        setEligibleToAdd(Object.values(profileList?.profiles ?? {})?.length < profileList?.profilesLimit);

        if (profileList?.profiles.length > 0) {
            setShowProfiles(true);
        }
    }, [selectedProfile, profileList, profileSwitched]);

    // clear store to hide toast msg
    useEffect(() => {
        if (
            dispatchedActions?.action !== 'hasBeenDeleted' &&
            typeof dispatchedActions.message === 'object' &&
            dispatchedActions?.message !== null
        ) {
            toast(
                __(`manage_profile_${dispatchedActions.action}_successfully`).replace(
                    '{profile Name}',
                    dispatchedActions.message?.profileName || ''
                ),
                'success'
            );

            setTimeout(() => {
                clearDispatchedActions();
            }, 2500);
        }
    }, [dispatchedActions]);

    // If the flag (Account retrieval feature) is enabled:
    //      Check if the profile is locked with parental lock
    // otherwise:
    //      Assume the profile is not locked with parental lock
    // const profileIsLocked = h.isAccountRetrievalFeatureEnabled() ? selectedProfile.parentalLockOn : false;

    // Because some localization on B2B is different, for example "Password" should be "Account PIN" on B2B
    // If account type is B2B, Then this will be used to add "b2b_" just before the localization key
    const B2B_localization = user.getUserSegment() === 'B2B' ? 'b2b_' : '';

    const language = useLanguage();
    const altLanguage = language === 'en' ? 'ar' : 'en';

    const visibleProfiles = (profileList?.profiles ?? []).filter((profile) => {
        if (showKidsOnly) {
            return profile.kidsMode;
        }
        return !selectedProfile.kidsMode || profile.kidsMode;
    });

    const uiProfiles: SelectProfileItem[] = visibleProfiles.map((profile) => {
        const avatarColor =
            typeof profile?.avatar === 'string' && profile.avatar.startsWith('#')
                ? profile.avatar
                : profileColors?.[profile?.avatar];

        return {
            id: profile.profileGuid,
            name: profile.profileName,
            rating: __('parentalControl_' + (profile.newParentalControl || profile.parentalControl)),
            avatar: getSelectedProfileAvatars(profile, profileAvatars),
            color: avatarColor,
        };
    });

    const canAddProfile = Boolean(eligibleToAdd && !selectedProfile.kidsMode && !showKidsOnly);
    const manageLabel = showKidsOnly ? undefined : showDoneBtn ? __('done') : __('manage_profiles');

    const handleSelectUiProfile = (item: SelectProfileItem) => {
        const profile = visibleProfiles.find((entry) => entry.profileGuid === item.id);
        if (profile) {
            selectProfileHandler(profile);
        }
    };

    const handleAddProfile = () => {
        navigate(url.route('add-profile'), {
            state: { fromRoute: url.getDetailsPage(screenDetailTypes.PROFILE_SELECT) },
        });
    };

    return (
        <div style={{ position: 'relative', minHeight: '100vh' }}>
            <SelectProfileUi
                profiles={uiProfiles}
                promos={promos}
                slideDelay={promoSlideDelay}
                prompt={editMode ? __('manage_profile_edit_your_profiles') : __('manage_profile_who_is_watching')}
                addLabel={__('add_profiles')}
                manageLabel={manageLabel}
                editing={editMode}
                language={{
                    label: altLanguage === 'ar' ? __('settings_lang_Arabic') : __('settings_lang_English'),
                    href: h.getAltLanguageURL(altLanguage, location),
                    onClick: (e) => handleSwitchLanguage(e, language),
                }}
                logoSrc="images/big-logo.svg"
                logoHref={url.route(selectedProfile?.kidsMode ? 'kids-home' : '')}
                LinkComponent={RouterLink}
                onSelect={handleSelectUiProfile}
                onAdd={canAddProfile ? handleAddProfile : undefined}
                onManage={showKidsOnly ? undefined : showDoneBtn ? handleEditProfilesDone : handleEditProfiles}
            />
            <PasswordPopup
                description={__(B2B_localization + 'password_popup_description_selectProfile')}
                showPopup={showPasswordPopup}
                profile={clickedProfile ? clickedProfile : {}}
                onSuccess={() => setPasswordVerified(true)}
                onClose={() => closePasswordPopupHandler()}
                verifyPasswordFunction={validatePasswordAndSwitchProfile}
                shouldDispatch={false}
            />
        </div>
    );
};
